/* eslint-disable camelcase */
/* eslint-disable no-unused-vars */

import {
  takeEvery, put, select, call, takeLatest,
} from 'redux-saga/effects';

import {
  MODALS, API_URLS, FETCH_OPTIONS, SUCCESS_MODALS,
} from '../constants';
import {
  requestGet, requestGetDownloadedFile, requestPost, requestPostFormData,
} from '../utils/request';
import { getCompetitorsFromState, getPlayersFromState, getSelectedPlayers } from '../selectors/teams';
import { aDisablePlayerRequest, aDownaloadLiveTemplateFileRequest, aDownloadPlayersTemplatesFile, aDownloadTennisLiveTemplateFileRequest, aGetCompetitorsRequest, aGetLiveTemplateFileRequest, aGetLiveTemplateRequest, aGetPlayersRequest, aGetPlayersTemplateFileRequest, aGetPlayersTemplateRequest, aGetTennisLiveTemplateFileRequest, aGetTennisLiveTemplateRequest, aSaveLiveTemplateFileRequest, aSaveLiveTemplateRequest, aSaveNewPlayerRequest, aSavePlayersTemplatePreviewRequest, aSavePlayersTemplateRequest, aSaveTennisLiveTemplateFileRequest, aSaveTennisLiveTemplateRequest, aSaveUpdatedSquadListRequest, aUpdatePlayersOnSquad, aUploadPlayersTemplateFileRequest } from '../reducers';
import { aSetIsFetching, aSetOpenModal } from '../reducers/common';
import { aDisablePlayerSuccess, aDownloadLiveTemplateFileSuccess, aDownloadPlayersTemplateFileSuccess, aDownloadTennisLiveTemplateFileSuccess, aGetCompetitorsSuccess, aGetLiveTemplateFileSuccess, aGetLiveTemplateSuccess, aGetPlayersSuccess, aGetPlayersTemplateFileSuccess, aGetPlayersTemplateSuccess, aGetTennisLiveTemplateFileSuccess, aGetTennisLiveTemplateSuccess, aSaveNewPlayerSuccess, aSavePlayersTemplatePreviewSuccess, aSaveUpdatedSquadListSuccess, aUpdatePlayersOnSquadSuccess } from '../reducers/teams';

/**
 * @memberof sagas/teams
 * @async
 * @param {object} action
 */
function* getCompetitorsSaga(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_COMPETITORS,
      value: true,
    }));

    response = yield call(requestGet, API_URLS.GET_COMPETITORS, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aGetCompetitorsSuccess({
        competitors: response.data.competitions,
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_COMPETITORS,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_COMPETITORS,
      value: false,
    }));
  }
}

/**
 * @memberof sagas/teams
 * @async
 * @param {object} action
 */
function* getPlayersSaga(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_PLAYERS,
      value: true,
    }));

    response = yield call(requestPost, API_URLS.GET_PLAYERS, action.payload, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      const selectedPlayers = yield select(getSelectedPlayers);

      const selectedPlayersFromSquad = response
        ?.data?.players?.filter((player) => player?.squad_position
          !== 0 && player?.squad_position !== null)
        ?.sort((a, b) => a?.squad_position - b?.squad_position);

      // populate selected players array: initially array of 11 players with empty details
      for (let i = 0; i < selectedPlayersFromSquad.length; i += 1) {
        selectedPlayers[i] = selectedPlayersFromSquad[i];
      }

      yield put(aGetPlayersSuccess({
        players: response.data.players,
        selectedPlayers,
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_PLAYERS,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_PLAYERS,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* saveNewPlayerSaga(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.SAVE_NEW_PLAYER,
      value: true,
    }));

    response = yield call(requestPost, API_URLS.SAVE_NEW_PLAYER, action.payload, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aSaveNewPlayerSuccess({
        players: response.data.players,
      }));

      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.SAVE_NEW_PLAYER,
        value: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.SAVE_NEW_PLAYER,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* disablePlayerSaga(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.DISABLE_PLAYER,
      value: true,
    }));

    response = yield call(requestPost, API_URLS.DISABLE_PLAYER, action.payload, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aDisablePlayerSuccess({
        players: response.data,
      }));

      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.DISABLE_PLAYER,
        value: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.DISABLE_PLAYER,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* updatePlayersOnSquadSaga(action) {
  const { playerIdToAdd, playerIdToRemove, squadPosition } = action.payload;
  const players = yield select(getPlayersFromState);

  // remove player from squad
  const removedPlayer = players.find((player) => player?.id === playerIdToRemove);
  if (removedPlayer) {
    removedPlayer.squad_position = null;
  }

  // add new player to squad
  const newPlayer = players.find((player) => player?.id === playerIdToAdd);
  newPlayer.squad_position = squadPosition;

  const selectedPlayers = yield select(getSelectedPlayers);
  // squadPosition corresponds to (index + 1) for the element in the array
  selectedPlayers[squadPosition - 1] = newPlayer;

  yield put(aUpdatePlayersOnSquadSuccess({
    players,
    selectedPlayers,
  }));
}

/**
 * @param {object} action
 */
function* saveUpdatedSquad(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.SAVE_UPDATED_SQUAD,
      value: true,
    }));

    const selectedPlayers = yield select(getSelectedPlayers);

    const updatedPlayers = selectedPlayers?.map((player) => ({
      ...player,
      playerId: player?.id,
      squadPosition: player?.squad_position,
    }));

    const data = {
      competitorId: action.payload?.competitorId,
      squad: updatedPlayers,
    };

    response = yield call(requestPost, API_URLS.SAVE_UPDATED_SQUAD, data, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      const allCompetitors = yield select(getCompetitorsFromState);
      const modifiedComp = allCompetitors
        ?.find((comp) => comp?.id === action.payload?.selectedLeague);

      if (modifiedComp) {
        const modifiedTeam = modifiedComp.competitors
          ?.find((team) => team?.id === action.payload.competitorId);

        if (modifiedTeam) {
          modifiedTeam.squadCompleted = true;
        }
      }

      yield put(aSaveUpdatedSquadListSuccess({
        competitors: allCompetitors,
      }));

      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.SAVE_UPDATED_SQUAD,
        value: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.SAVE_UPDATED_SQUAD,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* savePlayersTemplateSaga(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.SAVE_PLAYERS_TEMPLATE,
      value: true,
    }));
    response = yield call(requestPost, API_URLS.SAVE_PLAYERS_TEMPLATE, action.payload, true);

    if (response.status === -1) {
      const err = new Error(response.error.message);
      err.modal = MODALS.ERROR;
      throw err;
    } else {
      yield put(aGetPlayersTemplateRequest());
      yield put(aSetOpenModal({
        modal: MODALS.SUCCESS,
        modalData: {
          message: SUCCESS_MODALS.SAVE_PLAYERS_TEMPLATE,
          modal: SUCCESS_MODALS.SAVE_PLAYERS_TEMPLATE,
        },
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.SAVE_PLAYERS_TEMPLATE,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.message || MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.SAVE_PLAYERS_TEMPLATE,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* saveTennisLiveTemplateSaga(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.SAVE_TENNIS_LIVE_TEMPLATE,
      value: true,
    }));
    const body = {
      liveTemplateFileId: action.payload.playerTemplateFileId,
      competitionId: action.payload.competitionId,
    };
    response = yield call(requestPost, API_URLS.SAVE_TENNIS_LIVE_TEMPLATE, body, true);

    if (response.status === -1) {
      throw new Error(response.error.message);
    } else {
      yield put(aGetTennisLiveTemplateRequest());
      yield put(aSetOpenModal({
        modal: MODALS.SUCCESS,
        modalData: {
          message: SUCCESS_MODALS.SAVE_LIVE_TEMPLATE,
          modal: SUCCESS_MODALS.SAVE_LIVE_TEMPLATE,
        },
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.SAVE_TENNIS_LIVE_TEMPLATE,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: MODALS.GENERAL_ERROR,
      errorMessage: error.message || MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.SAVE_TENNIS_LIVE_TEMPLATE,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* saveLiveTemplateSaga(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.SAVE_LIVE_TEMPLATE,
      value: true,
    }));
    const body = {
      templateFileId: action.payload.playerTemplateFileId,
      competitionId: action.payload.competitionId,
    };
    const response = yield call(requestPost, API_URLS.SAVE_LIVE_TEMPLATE, body, true);

    if (response.status === -1) {
      throw new Error(response.error.message);
    } else {
      yield put(aGetLiveTemplateRequest());
      yield put(aSetOpenModal({
        modal: MODALS.SUCCESS,
        modalData: {
          message: SUCCESS_MODALS.SAVE_LIVE_TEMPLATE,
          modal: SUCCESS_MODALS.SAVE_LIVE_TEMPLATE,
        },
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.SAVE_LIVE_TEMPLATE,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: MODALS.GENERAL_ERROR,
      errorMessage: error.message || MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.SAVE_LIVE_TEMPLATE,
      value: false,
    }));
  }
}
/**
 * @memberof sagas/teams
 * @async
 */
function* getLiveTemplateSaga() {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.LIVE_TEMPLATE,
      value: true,
    }));
    const response = yield call(requestGet, API_URLS.GET_LIVE_TEMPLATE, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aGetLiveTemplateSuccess({
        liveTemplates: response.data.templates,
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.LIVE_TEMPLATE,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.message || MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.LIVE_TEMPLATE,
      value: false,
    }));
  }
}

/**
 * @memberof sagas/teams
 * @async
 */
function* getTennisLiveTemplateSaga() {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.TENNIS_LIVE_TEMPLATE,
      value: true,
    }));
    const response = yield call(requestGet, API_URLS.GET_TENNIS_LIVE_TEMPLATE, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aGetTennisLiveTemplateSuccess({
        liveTemplates: response.data.templates,
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.TENNIS_LIVE_TEMPLATE,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.message || MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.TENNIS_LIVE_TEMPLATE,
      value: false,
    }));
  }
}

/**
 * @memberof sagas/teams
 * @async
 */
function* getPlayersTemplateSaga() {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.PLAYERS_TEMPLATE,
      value: true,
    }));
    const response = yield call(requestGet, API_URLS.GET_PLAYERS_TEMPLATE, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aGetPlayersTemplateSuccess({
        players: response.data.templates,
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.PLAYERS_TEMPLATE,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.PLAYERS_TEMPLATE,
      value: false,
    }));
  }
}

/**
 * @memberof sagas/teams
 * @async
 */
function* getPlayersTemplateFileSaga() {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.PLAYERS_TEMPLATE_FILE,
      value: true,
    }));
    const response = yield call(requestGet, API_URLS.GET_PLAYERS_TEMPLATE_FILE, true);
    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aGetPlayersTemplateFileSuccess({
        templateFiles: response.data.template_files,
      }));
    }

    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.PLAYERS_TEMPLATE_FILE,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.PLAYERS_TEMPLATE_FILE,
      value: false,
    }));
  }
}
/**
 * @memberof sagas/teams
 * @async
 */
function* getLiveTemplateFileSaga() {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.LIVE_TEMPLATE_FILE,
      value: true,
    }));
    const response = yield call(requestGet, API_URLS.GET_LIVE_TEMPLATE_FILE, true);
    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aGetLiveTemplateFileSuccess({
        templateFiles: response.data.template_files,
      }));
    }

    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.LIVE_TEMPLATE_FILE,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.message || MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.LIVE_TEMPLATE_FILE,
      value: false,
    }));
  }
}

/**
 * @memberof sagas/teams
 * @async
 */
function* getTennisLiveTemplateFileSaga() {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.TENNIS_LIVE_TEMPLATE_FILE,
      value: true,
    }));
    const response = yield call(requestGet, API_URLS.GET_TENNIS_LIVE_TEMPLATE_FILE, true);
    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aGetTennisLiveTemplateFileSuccess({
        templateFiles: response.data.template_files,
      }));
    }

    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.TENNIS_LIVE_TEMPLATE_FILE,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.message || MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.TENNIS_LIVE_TEMPLATE_FILE,
      value: false,
    }));
  }
}
/**
 * @param {object} action
 */
function* savePlayersTemplateFileSaga(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.SAVE_PLAYERS_TEMPLATE_FILE,
      value: true,
    }));
    const params = new FormData();
    params.append('file', action.payload);
    const response = yield call(requestPostFormData,
      API_URLS.SAVE_PLAYERS_TEMPLATE_FILE, params, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aGetPlayersTemplateFileRequest());
      yield put(aSetOpenModal({
        modal: MODALS.SUCCESS,
        modalData: {
          message: SUCCESS_MODALS.UPLOAD_PLAYERS_TEMPLATES_FILE,
          modal: SUCCESS_MODALS.UPLOAD_PLAYERS_TEMPLATES_FILE,
        },
      }));
      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.SAVE_PLAYERS_TEMPLATE_FILE,
        value: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.message || MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.SAVE_PLAYERS_TEMPLATE_FILE,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* saveLiveTemplateFileSaga(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.SAVE_LIVE_TEMPLATE_FILE,
      value: true,
    }));
    const params = new FormData();
    params.append('file', action.payload);
    const response = yield call(requestPostFormData,
      API_URLS.SAVE_LIVE_TEMPLATE_FILE, params, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aGetLiveTemplateFileRequest());
      yield put(aSetOpenModal({
        modal: MODALS.SUCCESS,
        modalData: {
          message: SUCCESS_MODALS.UPLOAD_LIVE_TEMPLATES_FILE,
          modal: SUCCESS_MODALS.UPLOAD_LIVE_TEMPLATES_FILE,
        },
      }));
      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.SAVE_LIVE_TEMPLATE_FILE,
        value: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.message || MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.SAVE_LIVE_TEMPLATE_FILE,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* saveTennisLiveTemplateFileSaga(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.SAVE_TENNIS_LIVE_TEMPLATE_FILE,
      value: true,
    }));
    const params = new FormData();
    params.append('file', action.payload);
    const response = yield call(requestPostFormData,
      API_URLS.SAVE_TENNIS_LIVE_TEMPLATE_FILE, params, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aGetTennisLiveTemplateFileRequest());
      yield put(aSetOpenModal({
        modal: MODALS.SUCCESS,
        modalData: {
          message: SUCCESS_MODALS.UPLOAD_LIVE_TEMPLATES_FILE,
          modal: SUCCESS_MODALS.UPLOAD_LIVE_TEMPLATES_FILE,
        },
      }));
      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.SAVE_TENNIS_LIVE_TEMPLATE_FILE,
        value: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.message || MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.SAVE_TENNIS_LIVE_TEMPLATE_FILE,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* savePlayersTemplatePreviewSaga(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.SAVE_PLAYERS_TEMPLATE_PREVIEW,
      value: true,
    }));
    const response = yield call(requestPost,
      API_URLS.SAVE_PLAYERS_TEMPLATE_PREVIEW, action.payload, true);

    if (response.status === -1) {
      throw new Error(response.error.message);
    } else {
      yield put(aSavePlayersTemplatePreviewSuccess({
        playersTemplatePreview: response.data === true ? {} : response.data,
      }));
      yield put(aSetOpenModal({
        modal: MODALS.PLAYERS_TEMPLATE_ADD_PREVIEW,
        modalData: {
          ...action.payload,
        },
      }));
      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.SAVE_PLAYERS_TEMPLATE_PREVIEW,
        value: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.message || MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.SAVE_PLAYERS_TEMPLATE_PREVIEW,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* downloadPlayersTemplatesFileSaga(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.DOWNLOAD_PLAYERS_TEMPLATE_FILE,
      value: true,
    }));
    const response = yield call(
      requestGetDownloadedFile,
      `${API_URLS.DOWNLOAD_PLAYERS_TEMPLATE_FILE}?fileRef=${action.payload.fileRef}`, true,
    );

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aDownloadPlayersTemplateFileSuccess({
        downloadedTemplateFile: response,
      }));
      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.DOWNLOAD_PLAYERS_TEMPLATE_FILE,
        value: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.DOWNLOAD_PLAYERS_TEMPLATE_FILE,
      value: false,
    }));
  }
}
/**
 * @param {object} action
 */
function* downloadLiveTemplatesFileSaga(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.DOWNLOAD_LIVE_TEMPLATE_FILE,
      value: true,
    }));
    const response = yield call(
      requestGetDownloadedFile,
      `${API_URLS.DOWNLOAD_LIVE_TEMPLATE_FILE}?fileRef=${action.payload.fileRef}`, true,
    );

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aDownloadLiveTemplateFileSuccess({
        downloadedTemplateFile: response,
      }));
      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.DOWNLOAD_LIVE_TEMPLATE_FILE,
        value: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.message || MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.DOWNLOAD_LIVE_TEMPLATE_FILE,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* downloadTennisLiveTemplatesFileSaga(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.DOWNLOAD_TENNIS_LIVE_TEMPLATE_FILE,
      value: true,
    }));
    const response = yield call(
      requestGetDownloadedFile,
      `${API_URLS.DOWNLOAD_TENNIS_LIVE_TEMPLATE_FILE}?fileRef=${action.payload.fileRef}`, true,
    );

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aDownloadTennisLiveTemplateFileSuccess({
        downloadedTemplateFile: response,
      }));
      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.DOWNLOAD_TENNIS_LIVE_TEMPLATE_FILE,
        value: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.message || MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.DOWNLOAD_TENNIS_LIVE_TEMPLATE_FILE,
      value: false,
    }));
  }
}

/**
 * @memberof sagas/teams
 * @async
 * @returns {void}
 */
export default function* teamsSaga() {
  yield takeLatest(aGetCompetitorsRequest, getCompetitorsSaga);
  yield takeLatest(aGetPlayersRequest, getPlayersSaga);
  yield takeLatest(aSaveNewPlayerRequest, saveNewPlayerSaga);
  yield takeLatest(aDisablePlayerRequest, disablePlayerSaga);

  yield takeEvery(aUpdatePlayersOnSquad, updatePlayersOnSquadSaga);
  yield takeLatest(aSaveUpdatedSquadListRequest, saveUpdatedSquad);
  yield takeLatest(aSaveTennisLiveTemplateRequest, saveTennisLiveTemplateSaga);
  yield takeLatest(aSaveLiveTemplateRequest, saveLiveTemplateSaga);
  yield takeLatest(aGetLiveTemplateRequest, getLiveTemplateSaga);
  yield takeLatest(aGetTennisLiveTemplateRequest, getTennisLiveTemplateSaga);
  yield takeLatest(aGetTennisLiveTemplateFileRequest, getTennisLiveTemplateFileSaga);
  yield takeLatest(aGetLiveTemplateFileRequest, getLiveTemplateFileSaga);
  yield takeLatest(aSaveTennisLiveTemplateFileRequest, saveTennisLiveTemplateFileSaga);
  yield takeLatest(aDownaloadLiveTemplateFileRequest, downloadLiveTemplatesFileSaga);
  yield takeLatest(aDownloadTennisLiveTemplateFileRequest, downloadTennisLiveTemplatesFileSaga);
  yield takeLatest(aSaveLiveTemplateFileRequest, saveLiveTemplateFileSaga);
  yield takeLatest(aGetPlayersTemplateRequest, getPlayersTemplateSaga);
  yield takeLatest(aSavePlayersTemplateRequest, savePlayersTemplateSaga);
  yield takeLatest(aGetPlayersTemplateFileRequest, getPlayersTemplateFileSaga);
  yield takeLatest(aUploadPlayersTemplateFileRequest, savePlayersTemplateFileSaga);
  yield takeLatest(aDownloadPlayersTemplatesFile, downloadPlayersTemplatesFileSaga);
  yield takeLatest(aSavePlayersTemplatePreviewRequest, savePlayersTemplatePreviewSaga);
}

